import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { Observable } from 'rxjs/Rx';
import { ObservedValueOf } from 'rxjs/internal/types';
import { CharterDAO } from '../../../../core/services/dao/charterDAO';
import { CustomerSignAgreementData } from '../../../portal/charters/charter-payment/charter-payment.component';
import { ContainsSubscriptions } from '../../../../core/services/interfaces/contains-subscriptions';
import { Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../../core/pure-functions/rx-helper';
import {
  DomSanitizer,
  SafeResourceUrl,
  SafeUrl,
} from '@angular/platform-browser';
import { UserDAO } from '../../../../core/services/dao/userDAO';
import { CustomerSignAgreementDialogService } from '../../../shared/dialogs/customer-sign-agreement-dialog/customer-sign-agreement-dialog.service';
import { AppMessagingService } from '../../../../core/services/app-messaging.service';
import { StripePaymentDAO } from '../../../../core/services/dao/stripePaymentDAO';
import { EnvironmentalMediaService } from '../../../../core/services/environmental-media.service';
import { User } from 'anchor-shared/lib/model/entity/user';
import { Stripe } from '@stripe/stripe-js';
import { ExternalScriptService } from '../../../../core/services/stripe.service';

@Component({
  selector: 'app-charter-agreement-signing',
  templateUrl: './charter-agreement-signing.component.html',
  styleUrls: ['./charter-agreement-signing.component.scss'],
})
export class CharterAgreementSigningComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  charterId: number;
  templateData$: Observable<{
    customerCharter: ObservedValueOf<Observable<CustomerSignAgreementData>>;
  }>;
  customerCharter: CustomerSignAgreementData;
  agreementUrl: SafeResourceUrl;
  subs: Subscription[] = [];
  isMobile = false;
  clicked = false;
  noAuthSignatureComplete = false;
  cacheBuster: string;
  stepNumber: number;
  userIdentityVerification;
  verificationCompleted: boolean = false;
  stripe: any;
  user: User;
  stripeLoaded: boolean;

  constructor(
    private route: ActivatedRoute,
    private charterDAO: CharterDAO,
    private domSanitizer: DomSanitizer,
    private router: Router,
    private userDAO: UserDAO,
    private customerSignAgreementDialogService: CustomerSignAgreementDialogService,
    private appMessagingService: AppMessagingService,
    private readonly environmentalMediaService: EnvironmentalMediaService,
    private readonly stripePaymentDao: StripePaymentDAO,
    private externalScriptService: ExternalScriptService,
  ) {
    externalScriptService.loadStripe().then(() => {
      console.log('loaded in Component');
      this.stripeLoaded = true;
      // @ts-ignore is global
      this.stripe = Stripe(
        this.environmentalMediaService.getStripePublishableKey(),
      );
    });
    this.user = this.userDAO.getCurrentUser();
    if (this.user.verifiedStatus !== 'requires_input') {
      this.verificationCompleted = true;
    }
    let params = this.route.snapshot.queryParams;
    if (params['verification'] && params['verification'] === 'false') {
      this.verificationCompleted = true;
    }
    if (params['step'] && params['step'] === '2') {
      this.stepNumber = 2;
    } else if (params['step'] && params['step'] === '1') {
      this.stepNumber = 1;
    }
    this.charterId = this.route.snapshot.params.charterId;
    this.isMobile = window && window.screen && window.screen.width < 960;
    this.cacheBuster =
      '?cache=' + Math.floor(Math.random() * Math.floor(1000000));
  }

  ngOnInit() {
    this.initAsyncData();
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  getUserIdentityVerification() {
    this.stripePaymentDao
      .getIdentityVerification()
      .pipe(take(1))
      .subscribe(async (session) => {
        this.userIdentityVerification = session;
        const { error } = await this.stripe.verifyIdentity(
          session.client_secret,
        );
        console.log(error);
        if (!error) {
          this.verificationCompleted = true;
        }
      });
  }

  openAgreement(): void {
    window.open(this.customerCharter.agreementUrl + this.cacheBuster);
  }

  signAgreementClick(): void {
    this.clicked = true;
    const sub = this.customerSignAgreementDialogService
      .open(this.customerCharter.customerName)
      .subscribe((completedSigning) => {
        if (completedSigning) {
          const signSub = this.charterDAO
            .customerSignAgreement(this.charterId)
            .subscribe((signed) => {
              if (this.userDAO.getCurrentUser()) {
                this.router.navigate([
                  'customer/charter-details/' + this.charterId,
                ]);
              } else {
                this.noAuthSignatureComplete = true;
                this.appMessagingService.openSnack(
                  'Success! You have signed your charter agreement.',
                );
              }
            });
          this.subs.push(signSub);
        } else {
          this.clicked = false;
        }
      });
    this.subs.push(sub);
  }

  private initAsyncData(): void {
    this.templateData$ = this.charterDAO
      .getCustomerCharterDetailsForAgreementSigning(this.charterId)
      .pipe(
        map((customerCharter) => {
          this.customerCharter = customerCharter;
          if (
            customerCharter.agreementUrl &&
            customerCharter.signedAgreementUrl
          ) {
            this.customerCharter.agreementUrl += this.cacheBuster;
            this.agreementUrl =
              this.domSanitizer.bypassSecurityTrustResourceUrl(
                customerCharter.agreementUrl,
              );
          } else {
            const sub = this.charterDAO
              .createCharterAgreementForCustomer(this.charterId)
              .subscribe((createdCharterAgreement) => {
                this.customerCharter.agreementUrl =
                  createdCharterAgreement.agreementUrl;
                this.customerCharter.agreementUrl += this.cacheBuster;
                this.agreementUrl =
                  this.domSanitizer.bypassSecurityTrustResourceUrl(
                    createdCharterAgreement.agreementUrl,
                  );
              });
            this.subs.push(sub);
          }
          return { customerCharter };
        }),
      );
  }
}
